import React from 'react';
import styled from '@emotion/styled';
import {
    Box,
    Typography,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Button,
    IconButton,
    Grid,
    FormControlLabel,
    Switch
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';

const ConfigGrid = styled(Grid)(({ theme }) => ({
    marginTop: theme.spacing(1),
}));

const ColumnsHeader = styled(Box)(({ theme }) => ({
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
}));

const ColumnCard = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(1.5),
    padding: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
}));

const ColumnCardHeader = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(0.5),
}));

const FooterSection = styled(Box)(({ theme }) => ({
    marginTop: theme.spacing(2),
}));

const FooterToggles = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
});

const TableColumnEditor = ({ tableConfig, onUpdate }) => {
    const columns = tableConfig.columns || [];

    const handleConfigChange = (key, value) => {
        onUpdate({ ...tableConfig, [key]: value });
    };

    const handleColumnChange = (index, key, value) => {
        const updated = [...columns];
        updated[index] = { ...updated[index], [key]: value };
        onUpdate({ ...tableConfig, columns: updated });
    };

    const addColumn = () => {
        onUpdate({
            ...tableConfig,
            columns: [...columns, { key: '', label: '', widthRatio: 0.15, alignment: 'left', format: '' }],
        });
    };

    const removeColumn = (index) => {
        const updated = columns.filter((_, i) => i !== index);
        onUpdate({ ...tableConfig, columns: updated });
    };

    return (
        <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
                Table Configuration
            </Typography>

            <ConfigGrid container spacing={1}>
                <Grid item xs={6}>
                    <TextField
                        size="small"
                        fullWidth
                        label="Row Height"
                        type="number"
                        value={tableConfig.rowHeight || 18}
                        onChange={(e) => handleConfigChange('rowHeight', Number(e.target.value))}
                        inputProps={{ step: 1, min: 10 }}
                    />
                </Grid>
                <Grid item xs={6}>
                    <TextField
                        size="small"
                        fullWidth
                        label="Header Height"
                        type="number"
                        value={tableConfig.headerHeight || 22}
                        onChange={(e) => handleConfigChange('headerHeight', Number(e.target.value))}
                        inputProps={{ step: 1, min: 10 }}
                    />
                </Grid>
                <Grid item xs={6}>
                    <TextField
                        size="small"
                        fullWidth
                        label="Header Font Size"
                        type="number"
                        value={tableConfig.headerFontSize || 10}
                        onChange={(e) => handleConfigChange('headerFontSize', Number(e.target.value))}
                        inputProps={{ step: 0.5, min: 4 }}
                    />
                </Grid>
                <Grid item xs={6}>
                    <FormControl size="small" fullWidth>
                        <InputLabel>Header Weight</InputLabel>
                        <Select
                            value={tableConfig.headerFontWeight || 'bold'}
                            onChange={(e) => handleConfigChange('headerFontWeight', e.target.value)}
                            label="Header Weight"
                        >
                            <MenuItem value="normal">Normal</MenuItem>
                            <MenuItem value="bold">Bold</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
            </ConfigGrid>

            <ColumnsHeader>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    Columns ({columns.length})
                </Typography>
                <Button size="small" startIcon={<AddIcon />} onClick={addColumn}>
                    Add
                </Button>
            </ColumnsHeader>

            {columns.map((col, idx) => (
                <ColumnCard key={idx}>
                    <ColumnCardHeader>
                        <Typography variant="caption" fontWeight={600}>Column {idx + 1}</Typography>
                        <IconButton size="small" onClick={() => removeColumn(idx)}>
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </ColumnCardHeader>
                    <Grid container spacing={1}>
                        <Grid item xs={6}>
                            <TextField
                                size="small"
                                fullWidth
                                label="Key"
                                value={col.key || ''}
                                onChange={(e) => handleColumnChange(idx, 'key', e.target.value)}
                                placeholder="e.g. description"
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                size="small"
                                fullWidth
                                label="Label"
                                value={col.label || ''}
                                onChange={(e) => handleColumnChange(idx, 'label', e.target.value)}
                                placeholder="e.g. Description"
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <TextField
                                size="small"
                                fullWidth
                                label="Width %"
                                type="number"
                                value={Math.round((col.widthRatio || 0) * 100)}
                                onChange={(e) => handleColumnChange(idx, 'widthRatio', Number(e.target.value) / 100)}
                                inputProps={{ step: 5, min: 5, max: 100 }}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <FormControl size="small" fullWidth>
                                <InputLabel>Align</InputLabel>
                                <Select
                                    value={col.alignment || 'left'}
                                    onChange={(e) => handleColumnChange(idx, 'alignment', e.target.value)}
                                    label="Align"
                                >
                                    <MenuItem value="left">Left</MenuItem>
                                    <MenuItem value="center">Center</MenuItem>
                                    <MenuItem value="right">Right</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={4}>
                            <FormControl size="small" fullWidth>
                                <InputLabel>Format</InputLabel>
                                <Select
                                    value={col.format || ''}
                                    onChange={(e) => handleColumnChange(idx, 'format', e.target.value)}
                                    label="Format"
                                >
                                    <MenuItem value="">None</MenuItem>
                                    <MenuItem value="currency">Currency</MenuItem>
                                    <MenuItem value="date">Date</MenuItem>
                                    <MenuItem value="hours">Hours</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </ColumnCard>
            ))}

            <FooterSection>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    Footer Options
                </Typography>
                <FooterToggles>
                    <FormControlLabel
                        control={
                            <Switch
                                size="small"
                                checked={tableConfig.showSubtotal || false}
                                onChange={(e) => handleConfigChange('showSubtotal', e.target.checked)}
                            />
                        }
                        label={<Typography variant="body2">Show Subtotal</Typography>}
                    />
                    <FormControlLabel
                        control={
                            <Switch
                                size="small"
                                checked={tableConfig.showTax || false}
                                onChange={(e) => handleConfigChange('showTax', e.target.checked)}
                            />
                        }
                        label={<Typography variant="body2">Show Tax</Typography>}
                    />
                    <FormControlLabel
                        control={
                            <Switch
                                size="small"
                                checked={tableConfig.showTotal || false}
                                onChange={(e) => handleConfigChange('showTotal', e.target.checked)}
                            />
                        }
                        label={<Typography variant="body2">Show Total</Typography>}
                    />
                </FooterToggles>
            </FooterSection>
        </Box>
    );
};

export default TableColumnEditor;
